// apps/workflow/src/pages/designer/Dashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, CheckCircle, Clock, AlertCircle,
  TrendingUp, User, Mail, Phone, MapPin, FileText, Upload, Send,
  X, Eye, Download, RefreshCw, Check, Briefcase, CreditCard, Search, PartyPopper,
  DollarSign, Calendar, Award, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const DEFAULT_TASK_PRICE = 500; // fallback if task has no price

const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
    assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-800' },
    'in-progress': { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
    submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-800' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

// ─── Main Component ──────────────────────────────────────────────
const DesignerDashboard = () => {
  const { user, refreshUser } = useAuth();
  const userRole = user?.role || 'designer';
  const initialFetchDone = useRef(false);

  // ─── State ──────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFiles, setSubmitFiles] = useState([]);
  const [submitNote, setSubmitNote] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('available');
  const [assignedToMeOnly, setAssignedToMeOnly] = useState(false);
  const [filterMonth, setFilterMonth] = useState(''); // Monthly filter for completed tasks

  // ─── Computed (memoized) ──────────────────────────────────
  const isFreelancer = useMemo(() => userRole === 'freelancer', [userRole]);
  const isDesigner = useMemo(() => ['designer', 'admin', 'super_admin', 'manager'].includes(userRole), [userRole]);

  const subscriptionEnd = useMemo(() => user?.subscriptionEnd ? new Date(user.subscriptionEnd) : null, [user?.subscriptionEnd]);
  const hasActiveSubscription = useMemo(() => {
    if (!isFreelancer) return false;
    return subscriptionEnd && subscriptionEnd > new Date();
  }, [isFreelancer, subscriptionEnd]);
  const isExpired = useMemo(() => {
    if (!isFreelancer) return false;
    return !subscriptionEnd || subscriptionEnd <= new Date();
  }, [isFreelancer, subscriptionEnd]);

  // ─── Refresh user on mount ─────────────────────────────────
  useEffect(() => {
    if (typeof refreshUser === 'function') refreshUser();
  }, []); // Only once

  // ─── Fetch tasks ────────────────────────────────────────────
  const fetchTasks = useCallback(async () => {
    if (isFreelancer && isExpired) {
      setLoading(false);
      return;
    }
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const records = await pb.collection('project_tasks').getFullList({
        sort: '-created',
        expand: 'project,project.leadId,assignedTo,completedBy,submittedBy',
        requestKey: null,
      });
      setTasks(records);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching tasks:', error);
        if (error.status === 404) {
          toast.warning('Tasks module not configured yet. Please contact admin.');
        } else {
          toast.error('Failed to load tasks');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, isFreelancer, isExpired]);

  // ─── Fetch earnings (only for freelancers) ─────────────────
  const [earnings, setEarnings] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(false);

  const fetchEarnings = useCallback(async () => {
    if (!user?.id) return;
    if (!isFreelancer) return; // Only freelancers see earnings
    setEarningsLoading(true);
    try {
      const records = await pb.collection('designer_earnings').getFullList({
        filter: `designerId = "${user.id}"`,
        sort: '-weekStart',
        expand: 'taskId,projectId',
        requestKey: null,
      });
      setEarnings(records);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching earnings:', error);
        if (error.status !== 404) {
          toast.error('Failed to load earnings');
        }
      }
    } finally {
      setEarningsLoading(false);
    }
  }, [user, isFreelancer]);

  // ─── Only fetch once on mount ──────────────────────────────
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchTasks();
      if (isFreelancer) fetchEarnings();
    }
  }, [fetchTasks, fetchEarnings, isFreelancer]);

  // ─── Computed stats ──────────────────────────────────────
  const availableTasks = useMemo(() => {
    return tasks.filter(t => !t.assignedTo && t.status !== 'completed' && t.status !== 'submitted');
  }, [tasks]);

  const myTasks = useMemo(() => {
    return tasks.filter(t => t.assignedTo === user?.id);
  }, [tasks, user]);

  const inProgress = useMemo(() => {
    return tasks.filter(t => t.assignedTo === user?.id && t.status === 'in-progress');
  }, [tasks, user]);

  const completed = useMemo(() => {
    return tasks.filter(t => t.assignedTo === user?.id && t.status === 'completed');
  }, [tasks, user]);

  const total = tasks.length;

  const totalEarnings = earnings.reduce((sum, e) => sum + (e.amount || 0), 0);
  const paidEarnings = earnings.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.amount || 0), 0);
  const pendingEarnings = earnings.filter(e => e.status === 'pending').reduce((sum, e) => sum + (e.amount || 0), 0);

  // ─── Handlers ──────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTasks();
    if (isFreelancer) await fetchEarnings();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleClaimTask = async (taskId) => {
    if (isFreelancer && isExpired) {
      toast.error('Please subscribe to access tasks.');
      return;
    }
    try {
      await pb.collection('project_tasks').update(taskId, { assignedTo: user.id, status: 'assigned' });
      toast.success('Task claimed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to claim task');
    }
  };

  const handleStartWork = async (taskId) => {
    if (isFreelancer && isExpired) {
      toast.error('Please subscribe to access tasks.');
      return;
    }
    try {
      await pb.collection('project_tasks').update(taskId, { status: 'in-progress' });
      toast.success('Work started');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to start work');
    }
  };

  const handleSubmitDesign = async (e) => {
    e.preventDefault();
    if (submitFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    if (isFreelancer && isExpired) {
      toast.error('Please subscribe to submit designs.');
      return;
    }
    setSubmitting(true);
    try {
      for (const file of submitFiles) {
        const formData = new FormData();
        formData.append('task', selectedTask.id);
        formData.append('designer', user.id);
        formData.append('file', file);
        formData.append('version', '1');
        formData.append('status', 'pending-review');
        if (submitNote) formData.append('note', submitNote);
        await pb.collection('task_submissions').create(formData);
      }
      await pb.collection('project_tasks').update(selectedTask.id, {
        status: 'submitted',
        submittedBy: user.id,
        submittedAt: new Date().toISOString(),
      });
      toast.success('Design(s) submitted!');
      setShowSubmitModal(false);
      setSubmitFiles([]);
      setSubmitNote('');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to submit: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── UPDATED handleComplete – always creates earning ──────
  const handleComplete = async (taskId) => {
    if (isFreelancer && isExpired) {
      toast.error('Please subscribe to complete tasks.');
      return;
    }
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        toast.error('Task not found');
        return;
      }

      // Determine task price (fallback to default if missing)
      let taskPrice = task.price || DEFAULT_TASK_PRICE;
      if (!task.price) {
        toast.warning(`Task "${task.title}" has no price set. Using default ₹${DEFAULT_TASK_PRICE}.`);
      }
      const amount = taskPrice * 0.4; // 40% of task price

      // Calculate week boundaries (Monday – Sunday)
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday ...
      const diff = (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      // Check if earning already exists for this task (avoid duplicates)
      const existing = await pb.collection('designer_earnings').getFullList({
        filter: `taskId = "${taskId}"`,
        limit: 1,
      });
      if (existing.length === 0) {
        await pb.collection('designer_earnings').create({
          designerId: user.id,
          taskId: taskId,
          projectId: task.projectId,
          amount: amount,
          status: 'pending',
          weekStart: monday.toISOString(),
          weekEnd: sunday.toISOString(),
        });
      } else {
        // Already exists – just update task status and skip earning creation
        toast.info('Earning already recorded for this task.');
      }

      // Update task status to completed
      await pb.collection('project_tasks').update(taskId, {
        status: 'completed',
        completedBy: user.id,
        completedAt: now.toISOString(),
      });

      toast.success('Task completed! Earnings recorded.');
      fetchTasks();
      if (isFreelancer) fetchEarnings();
    } catch (error) {
      console.error('Complete error:', error);
      toast.error('Failed to complete task: ' + error.message);
    }
  };

  // ─── Filtering ────────────────────────────────────────────────
  const getFilteredTasks = (tab) => {
    let list = tab === 'available' ? availableTasks : myTasks;
    if (filterStatus !== 'all') list = list.filter(t => t.status === filterStatus);
    if (searchTerm) {
      list = list.filter(t =>
        t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.expand?.project?.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.expand?.project?.leadId?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (tab === 'my') {
      if (assignedToMeOnly) list = list.filter(t => t.assignedTo === user?.id);
      // Monthly filter: only for completed tasks
      if (filterMonth) {
        list = list.filter(t => {
          if (t.status !== 'completed') return false;
          const completedDate = t.completedAt ? new Date(t.completedAt) : null;
          if (!completedDate) return false;
          const monthStr = completedDate.toISOString().slice(0, 7); // YYYY-MM
          return monthStr === filterMonth;
        });
      }
    }
    return list;
  };

  const filteredTasks = getFilteredTasks(activeTab);

  const groupedByClient = useMemo(() => {
    if (activeTab !== 'available') return null;
    const groups = {};
    filteredTasks.forEach(task => {
      const client = task.expand?.project?.leadId;
      if (!client) return;
      const key = client.id;
      if (!groups[key]) {
        groups[key] = { client, tasks: [] };
      }
      groups[key].tasks.push(task);
    });
    return Object.values(groups);
  }, [filteredTasks, activeTab]);

  // ─── Early returns ──────────────────────────────────────────
  if (isFreelancer && isExpired) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-white rounded-xl shadow-sm border border-amber-200 p-8 text-center max-w-2xl mx-auto mt-10">
        <PartyPopper className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 mb-2">🎉 Congratulations!</h2>
        <p className="text-slate-600 max-w-md mb-4">
          Your application has been approved! Subscribe now to unlock full access to tasks and client details.
        </p>
        <div className="bg-slate-50 p-4 rounded-lg mb-6 w-full max-w-sm">
          <div className="flex justify-between"><span className="text-slate-600">Status</span><span className="font-medium text-amber-600">Approved – Pending Subscription</span></div>
          <div className="flex justify-between mt-1"><span className="text-slate-600">Price</span><span className="font-medium">₹500 / year</span></div>
        </div>
        <Link
          to="/checkout"
          state={{ subscriptionType: 'freelancer', amount: 500, plan: 'Freelancer Annual', duration: 365, redirect: '/designer-dashboard' }}
          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition shadow-md"
        >
          <CreditCard className="w-5 h-5" /> Subscribe Now – ₹500
        </Link>
        <p className="text-xs text-slate-400 mt-4">One‑time payment gives you full access for one year.</p>
      </div>
    );
  }

  if (loading || (isFreelancer && earningsLoading)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading...</p>
      </div>
    );
  }

  // ─── Main Render ────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6 text-indigo-600" />
            {isFreelancer ? 'Freelancer' : 'Designer'} Dashboard
          </h1>
          <p className="text-sm text-slate-500">
            {isFreelancer ? 'Claim tasks and submit designs. Limited client info.' : 'Manage your design tasks and submissions.'}
          </p>
          {isFreelancer && hasActiveSubscription && (
            <p className="text-xs text-green-600 mt-1">✅ Subscription valid until {subscriptionEnd.toLocaleDateString()}</p>
          )}
        </div>
        <button onClick={handleRefresh} disabled={refreshing} className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50">
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard title="Total Tasks" value={total} icon={ClipboardList} color="indigo" />
        <StatCard title="Available" value={availableTasks.length} icon={AlertCircle} color="amber" />
        <StatCard title="My Tasks" value={myTasks.length} icon={Clock} color="blue" />
        <StatCard title="In Progress" value={inProgress.length} icon={TrendingUp} color="purple" />
        <StatCard title="Completed" value={completed.length} icon={CheckCircle} color="green" />
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-2 rounded-xl shadow-sm border border-slate-200">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          <button onClick={() => setActiveTab('available')} className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'available' ? 'bg-white shadow text-indigo-600' : 'text-slate-600 hover:bg-slate-200'}`}>Available Tasks</button>
          <button onClick={() => setActiveTab('my')} className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'my' ? 'bg-white shadow text-indigo-600' : 'text-slate-600 hover:bg-slate-200'}`}>My Tasks</button>
          {/* Earnings tab only for freelancers */}
          {isFreelancer && (
            <button onClick={() => setActiveTab('earnings')} className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'earnings' ? 'bg-white shadow text-indigo-600' : 'text-slate-600 hover:bg-slate-200'}`}>Earnings</button>
          )}
        </div>
        <div className="flex-1 flex flex-wrap items-center gap-2">
          {activeTab !== 'earnings' && (
            <>
              <div className="relative flex-1 min-w-[150px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" />
              </div>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none">
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="in-progress">In Progress</option>
                <option value="submitted">Submitted</option>
                <option value="completed">Completed</option>
              </select>
              {activeTab === 'my' && (
                <>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" checked={assignedToMeOnly} onChange={(e) => setAssignedToMeOnly(e.target.checked)} />
                    Only my tasks
                  </label>
                  {/* Monthly filter – only for completed tasks */}
                  <input
                    type="month"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
                    placeholder="Filter by month"
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── Task List ────────────────────────────────────────── */}
      {activeTab !== 'earnings' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {activeTab === 'available' ? 'Available Tasks' : 'My Tasks'}
            <span className="text-sm font-normal text-slate-400 ml-2">({filteredTasks.length} tasks)</span>
          </h2>

          {filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No tasks found.</p>
              {activeTab === 'available' && (
                <p className="text-xs mt-1">Please ask the sales team to create projects and tasks.</p>
              )}
              {activeTab === 'my' && filterMonth && (
                <p className="text-xs mt-1">No completed tasks for the selected month.</p>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {activeTab === 'available' && groupedByClient ? (
                groupedByClient.map(({ client, tasks: clientTasks }) => (
                  <div key={client.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-indigo-500" />
                      <span className="font-semibold text-slate-800">{client.name}</span>
                      <span className="text-xs text-slate-400">(ID: {client.jobId || 'N/A'})</span>
                    </div>
                    <div className="space-y-3 ml-6">
                      {clientTasks.map(task => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          user={user}
                          isFreelancer={isFreelancer}
                          hasActiveSubscription={hasActiveSubscription}
                          onClaim={handleClaimTask}
                          onStart={handleStartWork}
                          onComplete={handleComplete}
                          onSubmit={() => { setSelectedTask(task); setShowSubmitModal(true); }}
                        />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                filteredTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    user={user}
                    isFreelancer={isFreelancer}
                    hasActiveSubscription={hasActiveSubscription}
                    onClaim={handleClaimTask}
                    onStart={handleStartWork}
                    onComplete={handleComplete}
                    onSubmit={() => { setSelectedTask(task); setShowSubmitModal(true); }}
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Earnings Tab ─────────────────────────────────────── */}
      {activeTab === 'earnings' && isFreelancer && (
        <div className="space-y-5">
          {/* Earnings Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl p-5 border border-indigo-200/50">
              <p className="text-xs font-medium text-indigo-600 uppercase tracking-wider">Total Earnings</p>
              <p className="text-2xl font-bold text-indigo-700 mt-1">₹{totalEarnings.toFixed(2)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-5 border border-green-200/50">
              <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Paid</p>
              <p className="text-2xl font-bold text-green-700 mt-1">₹{paidEarnings.toFixed(2)}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-5 border border-amber-200/50">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Pending</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">₹{pendingEarnings.toFixed(2)}</p>
            </div>
          </div>

          {/* Earnings List */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-600" />
              Earning History
            </h2>
            {earnings.length === 0 ? (
              <p className="text-center py-12 text-slate-400">No earnings recorded yet.</p>
            ) : (
              <div className="space-y-4">
                {earnings.map((earning) => {
                  const task = earning.expand?.taskId;
                  const project = earning.expand?.projectId;
                  const statusColor = earning.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';
                  const weekLabel = `${formatDate(earning.weekStart)} - ${formatDate(earning.weekEnd)}`;
                  return (
                    <div key={earning.id} className="flex flex-wrap items-center justify-between gap-3 p-4 border border-slate-200 rounded-lg hover:shadow-md transition">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800">{task?.title || 'Task'}</p>
                        <p className="text-sm text-slate-500">Project: {project?.name || 'N/A'}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Week: {weekLabel}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-indigo-600">₹{earning.amount?.toFixed(2)}</p>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor}`}>
                          {earning.status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                        </span>
                        {earning.paidDate && (
                          <p className="text-xs text-slate-400 mt-1">Paid: {formatDate(earning.paidDate)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Submit Modal ────────────────────────────────────── */}
      {showSubmitModal && selectedTask && (
        <SubmitModal
          task={selectedTask}
          submitting={submitting}
          submitFiles={submitFiles}
          setSubmitFiles={setSubmitFiles}
          submitNote={submitNote}
          setSubmitNote={setSubmitNote}
          onSubmit={handleSubmitDesign}
          onClose={() => { setShowSubmitModal(false); setSubmitFiles([]); setSubmitNote(''); }}
        />
      )}
    </div>
  );
};

// ─── StatCard ──────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-slate-500">{title}</p><p className="text-2xl font-bold text-slate-800 mt-1">{value}</p></div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
};

// ─── TaskCard ──────────────────────────────────────────────────
const TaskCard = ({ task, user, isFreelancer, hasActiveSubscription, onClaim, onStart, onComplete, onSubmit }) => {
  const status = getStatusBadge(task.status);
  const project = task.expand?.project;
  const client = project?.expand?.leadId;
  const assigned = task.expand?.assignedTo;
  const isAssignedToMe = task.assignedTo === user?.id;
  const canClaim = !task.assignedTo && task.status !== 'completed' && task.status !== 'submitted';
  const canStart = isAssignedToMe && task.status === 'assigned';
  const canSubmit = isAssignedToMe && task.status === 'in-progress';
  const canComplete = isAssignedToMe && task.status === 'submitted';
  const isDisabled = isFreelancer && !hasActiveSubscription;

  return (
    <div className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-800">{task.title}</h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>{status.label}</span>
          </div>
          <div className="mt-1 text-sm text-slate-500">
            <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{project?.projectName || 'Project'}</span>
            {client && <span className="flex items-center gap-1 ml-3"><User className="w-4 h-4" />{client.name}</span>}
            {assigned && <span className="flex items-center gap-1 ml-3">Assigned to: {assigned.name}</span>}
            {task.price && <span className="flex items-center gap-1 ml-3 text-amber-600">₹{task.price}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDisabled && <span className="text-xs text-amber-500 px-2 py-1 border border-amber-200 rounded-lg bg-amber-50">Subscribe to access</span>}
          {!isDisabled && canClaim && (
            <button onClick={() => onClaim(task.id)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition flex items-center gap-1">
              <Check className="w-4 h-4" /> Claim
            </button>
          )}
          {!isDisabled && canStart && <button onClick={() => onStart(task.id)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition">Start Work</button>}
          {!isDisabled && canSubmit && <button onClick={onSubmit} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition flex items-center gap-1"><Upload className="w-4 h-4" /> Submit</button>}
          {!isDisabled && canComplete && <button onClick={() => onComplete(task.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition">Complete</button>}
          <Link
            to={`/designer/task/${task.id}`}
            className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50 transition flex items-center gap-1"
          >
            <Eye className="w-4 h-4" /> View
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── SubmitModal ──────────────────────────────────────────────
const SubmitModal = ({ task, submitting, submitFiles, setSubmitFiles, submitNote, setSubmitNote, onSubmit, onClose }) => {
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSubmitFiles(files);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-800">Submit Design</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Task: <span className="font-medium text-slate-700">{task?.title}</span></p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Files *</label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
              required
              accept=".pdf,.dwg,.jpg,.jpeg,.png,.zip,.rar,.dwg,.dxf"
            />
            <p className="text-xs text-slate-400 mt-1">Accepted: PDF, DWG, JPG, PNG, ZIP, RAR</p>
            {submitFiles.length > 0 && (
              <p className="text-xs text-green-600 mt-1">{submitFiles.length} file(s) selected</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Note (optional)</label>
            <textarea
              value={submitNote}
              onChange={(e) => setSubmitNote(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
              placeholder="Any comments about this design..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" /> {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DesignerDashboard;